import {isPlatformBrowser} from '@angular/common';
import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Profile} from '@models';
import {getDownloadURL, ref as storeRef} from 'firebase/storage';
import {FirebaseInitService} from '../shared/firebase-init.service';

export interface ResolvedProfileImage {
	url: string;
	cache: boolean;
}

@Injectable()
export class ProfileImageService {
	private readonly profileImageCachePrefix = 'profileImageUrl:';
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);

	private get storage() {
		return this.firebaseInit.storage;
	}

	getCacheKey(uid: string): string {
		return `${this.profileImageCachePrefix}${uid}`;
	}

	getCachedUrl(uid: string): string | null {
		if (!this.isBrowser) {
			return null;
		}
		return localStorage.getItem(this.getCacheKey(uid));
	}

	setCachedUrl(uid: string, url: string): void {
		if (!this.isBrowser) {
			return;
		}
		localStorage.setItem(this.getCacheKey(uid), url);
	}

	clearCachedUrl(uid: string): void {
		if (!this.isBrowser) {
			return;
		}
		localStorage.removeItem(this.getCacheKey(uid));
	}

	resolveProfileImage(profile: Profile): Promise<ResolvedProfileImage | null> {
		if (!profile?.uuid || !profile.hasImage) {
			return Promise.resolve(null);
		}

		const hasHttpUrl = !!profile.imgUrl && /^https?:\/\//i.test(profile.imgUrl);
		const hasDownloadToken = hasHttpUrl && /[?&]token=/.test(profile.imgUrl);
		if (hasHttpUrl && hasDownloadToken && profile.imgUrl !== Profile.defaultImg) {
			this.setCachedUrl(profile.uuid, profile.imgUrl);
			return Promise.resolve({url: profile.imgUrl, cache: false});
		}

		const cachedUrl = this.getCachedUrl(profile.uuid);
		if (cachedUrl && /^https?:\/\//i.test(cachedUrl)) {
			return Promise.resolve({url: cachedUrl, cache: false});
		}

		return this.fetchProfileImageUrl(profile.uuid)
			.then((url) => (url ? {url, cache: true} : null))
			.catch(() => null);
	}

	protected fetchProfileImageUrl(uid: string): Promise<string> {
		return getDownloadURL(storeRef(this.storage, `users-gallery/${uid}/profilePicture.jpeg`));
	}
}
